import React, { useState } from "react";
import { Check } from "lucide-react";
import { Avatar } from "@/components/Podium";

export const teamLabel = (teams, id) => {
  const t = teams.find((x) => x.id === id);
  return t ? `${t.player1.name} & ${t.player2.name}` : "A definir";
};

function TeamLine({ team, score, win, showScore, bye }) {
  return (
    <div className={`flex items-center justify-between gap-2 ${win ? "font-bold text-[#1B7A45]" : "text-slate-700"}`}>
      <div className="flex min-w-0 items-center gap-2">
        {team ? <div className="flex shrink-0 -space-x-2">{[team.player1, team.player2].map((p, i) => <Avatar key={i} r={p} size="h-6 w-6 ring-2 ring-white text-[10px]" />)}</div> : <span className="h-6 w-6 shrink-0 rounded-full bg-slate-100" />}
        <span className="truncate text-sm">{team ? `${team.player1.name} & ${team.player2.name}` : bye ? "Folga" : "A definir"}</span>
      </div>
      {showScore && <span className="font-display text-lg font-black">{score}</span>}
    </div>
  );
}

function MatchCard({ m, teams, editable, onScore }) {
  const [a, setA] = useState(m.score_a || "");
  const [b, setB] = useState(m.score_b || "");
  const ta = teams.find((x) => x.id === m.team_a);
  const tb = teams.find((x) => x.id === m.team_b);
  const done = m.status === "finalizado";
  const ready = ta && tb && m.status !== "bye";
  return (
    <div data-testid={`tmatch-${m.id}`} className={`w-full min-w-[220px] rounded-2xl border bg-white p-3 ${done ? "border-emerald-200" : "border-slate-200"}`}>
      <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{m.label}{m.status === "bye" ? " · avançou direto" : ""}</p>
      <div className="space-y-1.5">
        <TeamLine team={ta} score={m.score_a} win={m.winner === "a"} showScore={done} bye={m.status === "bye"} />
        <TeamLine team={tb} score={m.score_b} win={m.winner === "b"} showScore={done} bye={m.status === "bye"} />
      </div>
      {editable && ready && (
        <div className="mt-2 flex items-center gap-1.5">
          <input data-testid={`tmatch-a-${m.id}`} type="number" min="0" value={a} onChange={(e) => setA(e.target.value)} className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-center text-sm" />
          <span className="text-xs text-slate-400">x</span>
          <input data-testid={`tmatch-b-${m.id}`} type="number" min="0" value={b} onChange={(e) => setB(e.target.value)} className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-center text-sm" />
          <button data-testid={`tmatch-save-${m.id}`} onClick={() => onScore(m, Number(a), Number(b))} className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1B7A45] px-3 py-1 text-xs font-bold text-white hover:bg-[#156337]"><Check className="h-3.5 w-3.5" /> Salvar</button>
        </div>
      )}
    </div>
  );
}

function Standings({ group, teams }) {
  return (
    <div data-testid={`tgroup-${group.name}`} className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <p className="border-b border-slate-100 px-3 py-2 font-display text-sm font-bold text-slate-900">{group.name === "Geral" ? "Classificação" : `Grupo ${group.name}`}</p>
      <table className="w-full text-xs">
        <thead><tr className="text-slate-400"><th className="px-3 py-1.5 text-left">#</th><th className="text-left">Dupla</th><th>J</th><th>V</th><th>D</th><th className="pr-3">Saldo</th></tr></thead>
        <tbody>
          {group.standings.map((r, i) => (
            <tr key={r.team_id} className="border-t border-slate-50 text-center text-slate-700">
              <td className="px-3 py-1.5 text-left font-bold">{i + 1}</td>
              <td className="max-w-[180px] truncate text-left font-semibold">{teamLabel(teams, r.team_id)}</td>
              <td>{r.played}</td><td className="font-bold text-[#1B7A45]">{r.wins}</td><td>{r.losses}</td><td className="pr-3">{r.diff}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function TournamentBoard({ data, cat, editable = false, onScore }) {
  const matches = data.matches.filter((m) => m.category_id === cat.id);
  const teams = data.teams;
  const rr = matches.filter((m) => m.stage !== "ko");
  const ko = matches.filter((m) => m.stage === "ko");
  const rounds = [...new Set(ko.filter((m) => !m.is_third).map((m) => m.round))].sort((x, y) => x - y);
  const results = data.results.filter((r) => r.category_id === cat.id);
  const place = (k) => [...new Set(results.filter((r) => r.placement === k).map((r) => r.team_id))];

  if (!cat.format) return <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-8 text-center text-sm text-slate-400">O sorteio desta categoria ainda não foi feito.</p>;

  return (
    <div className="space-y-5" data-testid={`tboard-${cat.id}`}>
      {cat.finished && (
        <div data-testid="tboard-champion" className="rounded-3xl bg-gradient-to-br from-amber-300 to-amber-500 p-4 text-center text-white shadow-lg">
          <p className="text-xs font-bold uppercase tracking-widest">Campeões</p>
          <p className="font-display text-2xl font-black">{place("champion").map((id) => teamLabel(teams, id)).join(", ")}</p>
          <p className="mt-1 text-xs font-semibold text-white/90">Vice: {place("vice").map((id) => teamLabel(teams, id)).join(", ")}{place("third").length ? ` · 3º: ${place("third").map((id) => teamLabel(teams, id)).join(", ")}` : ""}</p>
        </div>
      )}
      {cat.groups?.length > 0 && <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{cat.groups.map((g) => <Standings key={g.name} group={g} teams={teams} />)}</div>}
      {rr.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Jogos</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{rr.map((m) => <MatchCard key={`${m.id}-${m.status}`} m={m} teams={teams} editable={editable} onScore={onScore} />)}</div>
        </div>
      )}
      {ko.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Mata-mata</p>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {rounds.map((r) => (
              <div key={r} className="flex shrink-0 flex-col justify-around gap-3">
                {ko.filter((m) => m.round === r && !m.is_third).sort((x, y) => x.slot - y.slot).map((m) => <MatchCard key={`${m.id}-${m.status}-${m.team_a}-${m.team_b}`} m={m} teams={teams} editable={editable} onScore={onScore} />)}
              </div>
            ))}
            {ko.filter((m) => m.is_third).map((m) => <div key={m.id} className="flex shrink-0 flex-col justify-end"><MatchCard key={`${m.id}-${m.status}-${m.team_a}-${m.team_b}`} m={m} teams={teams} editable={editable} onScore={onScore} /></div>)}
          </div>
        </div>
      )}
    </div>
  );
}
